import { NavContentBlueprint } from '@backstage/plugin-app-react';
import { NavLink } from 'react-router-dom';
import { makeStyles } from '@mui/styles';
import RocketIcon from '@mui/icons-material/Rocket';
import ViewList from '@mui/icons-material/ViewListRounded';
import GroupIcon from '@mui/icons-material/People';
import PaletteIcon from '@mui/icons-material/Palette';
import ApiIcon from '@mui/icons-material/Extension';
import ComponentIcon from '@mui/icons-material/Memory';
import DomainIcon from '@mui/icons-material/Apartment';
import ResourceIcon from '@mui/icons-material/Work';
import SystemIcon from '@mui/icons-material/Category';
import UserIcon from '@mui/icons-material/Person';
import BuildIcon from '@mui/icons-material/Build';
import PreviewIcon from '@mui/icons-material/Visibility';
import LogoFull from '../components/LogoFull';
import LogoIcon from '../components/LogoIcon';
import { SearchModal } from '../components/SearchModal';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarSpace,
  Link,
  useSidebarOpenState,
  SidebarSubmenu,
  SidebarSubmenuItem,
} from '@backstage/core-components';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import { Shortcuts } from '@backstage-community/plugin-shortcuts';
import { SidebarRagModal } from '@roadiehq/rag-ai';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import { FeatureFlagged } from '@backstage/core-app-api';
import { RiMenuLine, RiSearchLine } from '@remixicon/react';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: '100%',
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: -14,
  },
  link: {
    display: 'flex',
    alignItems: 'center',
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link
        component={NavLink}
        to="/"
        underline="none"
        className={classes.link}
        aria-label="Home"
      >
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

export const SidebarContent = NavContentBlueprint.make({
  params: {
    component: ({ navItems }) => {
      const nav = navItems.withComponent(item => (
        <SidebarItem icon={() => item.icon} to={item.href} text={item.title} />
      ));

      nav.take('page:search'); // Using search modal instead

      return (
        <Sidebar>
          <SidebarLogo />
          <SidebarGroup label="Search" icon={<RiSearchLine />} to="/search">
            <SidebarSearchModal>
              {({ toggleModal }) => <SearchModal toggleModal={toggleModal} />}
            </SidebarSearchModal>
          </SidebarGroup>
          <SidebarRagModal />
          <SidebarDivider />
          <SidebarGroup label="Menu" icon={<RiMenuLine />}>
            {nav.take('page:home')}
            <MyGroupsSidebarItem
              singularTitle="My Group"
              pluralTitle="My Groups"
              icon={GroupIcon}
            />
            {nav.take('page:service-portal')}
            {nav.take('page:scaffolder')}
            <SidebarItem icon={ViewList} to="catalog" text="Catalog">
              <SidebarSubmenu title="Catalog">
                <SidebarSubmenuItem
                  title="Components"
                  to="catalog?filters[kind]=component"
                  icon={ComponentIcon}
                />
                <SidebarSubmenuItem
                  title="APIs"
                  to="catalog?filters[kind]=api"
                  icon={ApiIcon}
                />
                <SidebarSubmenuItem
                  title="Systems"
                  to="catalog?filters[kind]=system"
                  icon={SystemIcon}
                />
                <SidebarSubmenuItem
                  title="Domains"
                  to="catalog?filters[kind]=domain"
                  icon={DomainIcon}
                />
                <FeatureFlagged with="features-entity-enabled">
                  <SidebarSubmenuItem
                    title="Features"
                    to="catalog?filters[kind]=feature"
                    icon={PaletteIcon}
                  />
                  <SidebarDivider />
                </FeatureFlagged>
                <SidebarSubmenuItem
                  title="Resources"
                  to="catalog?filters[kind]=resource"
                  icon={ResourceIcon}
                />
                <SidebarDivider />
                <SidebarSubmenuItem
                  title="Groups"
                  to="catalog?filters[kind]=group"
                  icon={GroupIcon}
                />
                <SidebarSubmenuItem
                  title="Users"
                  to="catalog?filters[kind]=user"
                  icon={UserIcon}
                />
              </SidebarSubmenu>
            </SidebarItem>
            {nav.take('page:techdocs')}
            {nav.take('page:api-docs')}
            {nav.take('page:explore')}
            <SidebarItem
              icon={RocketIcon}
              to="getting-started"
              text="Getting Started"
            />
            {nav.take('page:announcements')}
            <SidebarDivider />
          </SidebarGroup>
          <SidebarGroup label="More" icon={<RiMenuLine />}>
            <SidebarItem icon={BuildIcon} text="More">
              <SidebarSubmenu title="Tools">
                <SidebarSubmenuItem
                  icon={PreviewIcon}
                  to="entity-preview"
                  title="Entity Preview"
                />
                <SidebarSubmenuItem
                  icon={BuildIcon}
                  to="devtools"
                  title="DevTools"
                />
              </SidebarSubmenu>
            </SidebarItem>
            <SidebarDivider />
            <Shortcuts />
          </SidebarGroup>
          <SidebarSpace />
          <SidebarDivider />
          <SidebarGroup
            label="Settings"
            icon={<UserSettingsSignInAvatar />}
            to="/settings"
          >
            {nav.take('page:user-settings')}
          </SidebarGroup>
        </Sidebar>
      );
    },
  },
});
